import * as Sentry from '@sentry/browser'
import { z } from 'zod'
import { kTranscriptionApiPath } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getResponseDebugContext, getTopLevelKeys } from '@/util/api/response-body'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { agentV2Request } from '@/agent-v2/conversations/fetch/agent-v2-request'

const TranscribeAudioResponseSchema = z.object({ text: z.string() })

/**
 * Transcribe a recorded audio clip to text.
 * @param blob Recorded audio. Its MIME type tells the API which container it received.
 * @param filename Filename for the multipart part. Present for logging; the API keys off the type.
 * @param signal Aborts the upload when the recording is no longer wanted, such as on unmount.
 */
export async function postTranscribeAudio(blob: Blob, filename: string, signal?: AbortSignal): Promise<string> {
  const url = `${kPaxtonAppApiRESTBaseUrl()}${kTranscriptionApiPath}`
  const formData = new FormData()
  formData.append('file', blob, filename)

  return agentV2Request({
    url,
    method: 'POST',
    body: formData,
    sentryEndpoint: 'POST /api/v2/transcribe',
    signal,
    parseResponse: (data, { response, sentryTags }) => {
      const parsed = TranscribeAudioResponseSchema.safeParse(data)
      if (parsed.success) return parsed.data.text

      Sentry.withScope((scope) => {
        scope.setTags(sentryTags)
        Sentry.captureException(new Error('Transcribe response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, url),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid transcribe response shape', parsed.error.issues)
    },
  })
}
